import { useState, useRef, useCallback, useEffect } from "react";
import { DIETARY_MAP } from "../lib/dietary";
import { impactLight, impactHeavy, notificationSuccess } from "../lib/haptics";

const SWIPE_THRESHOLD = 100;
const EXIT_DURATION = 300;
const SPRING_TRANSITION = "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)";
const EXIT_TRANSITION = "transform 0.3s ease-out, opacity 0.3s ease-out";

export default function SwipeCard({ recipe, onSwipe, onTap, onUndo, active = true, showControls = true }) {
  const [dragX, setDragX] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [transition, setTransition] = useState("");
  const dragging = useRef(false);
  const startX = useRef(0);
  const cardRef = useRef(null);
  const thresholdCrossed = useRef(false);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const rotation = prefersReducedMotion ? 0 : dragX * 0.1;
  const likeOpacity = Math.min(Math.max(dragX / SWIPE_THRESHOLD, 0), 1);
  const dislikeOpacity = Math.min(Math.max(-dragX / SWIPE_THRESHOLD, 0), 1);

  const animateExit = useCallback(
    (liked) => {
      if (isAnimating) return;
      setIsAnimating(true);
      if (liked) notificationSuccess();
      else impactHeavy();
      const dir = liked ? 1 : -1;
      setTransition(prefersReducedMotion ? "none" : EXIT_TRANSITION);
      setDragX(dir * 150 * (window.innerWidth / 100));

      const timer = setTimeout(() => {
        onSwipe(liked);
        setDragX(0);
        setTransition("");
        setIsAnimating(false);
      }, prefersReducedMotion ? 0 : EXIT_DURATION);

      return () => clearTimeout(timer);
    },
    [isAnimating, onSwipe, prefersReducedMotion],
  );

  const onPointerDown = useCallback(
    (e) => {
      if (!active || isAnimating) return;
      dragging.current = true;
      startX.current = e.clientX;
      thresholdCrossed.current = false;
      setTransition("");
      cardRef.current?.setPointerCapture(e.pointerId);
    },
    [active, isAnimating],
  );

  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - startX.current;
    if (!thresholdCrossed.current && Math.abs(dx) >= SWIPE_THRESHOLD) {
      thresholdCrossed.current = true;
      impactLight();
    }
    setDragX(dx);
  }, []);

  const onPointerUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;

    if (Math.abs(dragX) >= SWIPE_THRESHOLD) {
      animateExit(dragX > 0);
    } else if (Math.abs(dragX) < 10 && !isAnimating && onTap) {
      setDragX(0);
      onTap(recipe);
    } else {
      setTransition(prefersReducedMotion ? "none" : SPRING_TRANSITION);
      setDragX(0);
      if (thresholdCrossed.current) impactLight();
    }
  }, [dragX, animateExit, prefersReducedMotion, isAnimating, onTap, recipe]);

  useEffect(() => {
    const handlePointerCancel = () => {
      if (dragging.current) {
        dragging.current = false;
        setTransition(prefersReducedMotion ? "none" : SPRING_TRANSITION);
        setDragX(0);
      }
    };
    window.addEventListener("pointercancel", handlePointerCancel);
    return () => window.removeEventListener("pointercancel", handlePointerCancel);
  }, [prefersReducedMotion]);

  const name = recipe?.name || "Recipe";
  const description = recipe?.description || "";
  const cuisine = recipe?.cuisine;
  const category = recipe?.category;
  const prepTime = recipe?.prep_time_min;
  const cookTime = recipe?.cook_time_min;
  const totalTime = (prepTime || 0) + (cookTime || 0);
  const tags = recipe?.tags || [];
  const dietaryTags = tags.filter(t => DIETARY_MAP[t]);

  return (
    <div className="flex flex-col items-center gap-6 w-full">
      <div className="scout-card-frame w-full max-w-sm shadow-xl shadow-glow-sm">
        <div
          ref={cardRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          role="button"
          tabIndex={active ? 0 : -1}
          aria-label={`${name}. Swipe right to like, left to pass.`}
          className="scout-card-inner relative w-full select-none"
          style={{
            touchAction: "none",
            transform: `translateX(${dragX}px) rotate(${rotation}deg)`,
            transition,
            cursor: active && !isAnimating ? "grab" : "default",
            pointerEvents: isAnimating ? "none" : "auto",
          }}
        >
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" style={{ opacity: likeOpacity, background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.18))' }}>
            <div className="bg-match-500 rounded-2xl px-8 py-3 rotate-[-12deg] shadow-lg border-2 border-match-400">
              <span className="font-display text-3xl font-semibold text-white">Like</span>
            </div>
          </div>
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" style={{ opacity: dislikeOpacity, background: 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(239,68,68,0.15))' }}>
            <div className="bg-red-500 rounded-2xl px-8 py-3 rotate-[12deg] shadow-lg border-2 border-red-400">
              <span className="font-display text-3xl font-semibold text-white">Skip</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-surface to-panel px-4 py-1.5 flex items-center justify-between">
            <span className="font-display text-[10px] font-bold text-lime">Recipe</span>
            {category && <span className="font-body text-[10px] font-semibold text-ink-muted">{category}</span>}
          </div>

          <div className="bg-surface px-4 py-2.5 flex items-center justify-around divide-x divide-hairline border-y border-hairline">
            {totalTime > 0 && (
              <div className="flex-1 text-center px-2">
                <p className="font-display text-sm font-semibold text-primary-text tabular-nums">{totalTime}m</p>
                <p className="font-body text-[9px] text-ink-muted">Time</p>
              </div>
            )}
            {cuisine && (
              <div className="flex-1 text-center px-2">
                <p className="font-display text-sm font-semibold text-ink truncate">{cuisine}</p>
                <p className="font-body text-[9px] text-ink-muted">Style</p>
              </div>
            )}
            {prepTime > 0 && (
              <div className="flex-1 text-center px-2">
                <p className="font-display text-sm font-semibold text-ink tabular-nums">{prepTime}m</p>
                <p className="font-body text-[9px] text-ink-muted">Prep</p>
              </div>
            )}
          </div>

          <div className="px-5 py-4 flex flex-col gap-2.5 h-56 overflow-hidden">
            <h2 className="font-display text-xl font-bold text-ink line-clamp-1">{name}</h2>
            {dietaryTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {dietaryTags.map(tag => (
                  <span key={tag} className="font-body text-xs font-medium px-2 py-0.5 rounded-full bg-lime/10 text-lime border border-lime/40">
                    {DIETARY_MAP[tag].emoji} {DIETARY_MAP[tag].label}
                  </span>
                ))}
              </div>
            )}
            {description && <p className="font-body text-sm text-ink-soft line-clamp-2 leading-relaxed">{description}</p>}
            {tags.filter(t => !DIETARY_MAP[t]).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {tags.filter(t => !DIETARY_MAP[t]).map((tag) => (
                  <span key={tag} className="font-body text-xs font-medium px-2.5 py-0.5 rounded-full bg-surface text-ink-soft border border-hairline">{tag}</span>
                ))}
              </div>
            )}
            {onTap && <p className="text-xs text-ink-muted text-center pt-1 mt-auto">Tap for details</p>}
          </div>
        </div>
      </div>

      {/* Tap controls — a clear, swipe-free way to decide. On mobile they're
          pinned just above the nav so they're always in view while deciding;
          on desktop they sit under the card. Swiping is an optional shortcut. */}
      {showControls && (
      <div className="flex items-start justify-center gap-6 fixed inset-x-0 bottom-28 z-30 lg:static lg:bottom-auto lg:z-auto">
        <div className="flex flex-col items-center gap-1.5">
          <button type="button" onClick={() => animateExit(false)} disabled={!active || isAnimating} aria-label="Skip this recipe"
            className="group w-16 h-16 rounded-full bg-panel border-2 border-red-200 flex items-center justify-center shadow-lg shadow-red-500/10 hover:shadow-red-500/20 active:scale-90 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-7 h-7 text-red-400 group-hover:text-red-500 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="font-display text-xs font-bold text-red-400">Skip</span>
        </div>
        {onUndo && (
          <button type="button" onClick={onUndo} disabled={!active || isAnimating} aria-label="Undo"
            className="mt-3 w-10 h-10 rounded-full bg-surface border border-hairline flex items-center justify-center hover:bg-surface active:scale-90 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
            </svg>
          </button>
        )}
        <div className="flex flex-col items-center gap-1.5">
          <button type="button" onClick={() => animateExit(true)} disabled={!active || isAnimating} aria-label="Like this recipe"
            className="group w-16 h-16 rounded-full bg-match-500 border-2 border-match-400 flex items-center justify-center shadow-lg shadow-match-500/20 hover:shadow-match-500/30 active:scale-90 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
            <svg className="w-7 h-7 text-white transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </button>
          <span className="font-display text-xs font-bold text-match-500">Like</span>
        </div>
      </div>
      )}
    </div>
  );
}
